/**
 * Predição e reconciliação do PRÓPRIO avatar.
 *
 * O problema: entre o dedo e a resposta na tela existe uma ida e volta de rede.
 * Esperar por ela é o jogo respondendo com atraso; a saída é o cliente aplicar o
 * input NA HORA com a mesma física do servidor, e corrigir quando o snapshot
 * autoritativo chegar dizendo o que de fato aconteceu.
 *
 * Invariante 2 continua de pé: NADA aqui vai para o fio. Isto é uma OPINIÃO local
 * sobre o futuro imediato, jogada fora toda vez que a autoridade fala. O que sai
 * do cliente continua sendo só intenção, por `connection.sendIntent`.
 *
 * Invariante 1: reexecuta com o `step` de `@game/sim`, o MESMO do servidor.
 * Reimplementar a física aqui seria garantir divergência na primeira mudança de
 * balanceamento.
 *
 * Caminho quente, a 30 Hz: nada aqui aloca depois do construtor.
 */
import { balance } from '@game/gamedata';
import { INVARIANT_BUDGETS } from '@game/gamedata/perf';
import {
  ACTION_MAGNET,
  bodyKindFromCode,
  createState,
  step,
  type GameState,
  type HazardZone,
  type PlayerInput,
} from '@game/sim';
import type { MessageData } from '@game/protocol';

type AuthoritativeSnapshot = MessageData<'snapshot'>;

/** O que o jogador quis, guardado até o servidor confirmar. */
export interface PendingInput {
  readonly seq: number;
  readonly moveX: number;
  readonly moveZ: number;
  readonly actions: number;
}

/**
 * Quantos inputs cabem "no ar" antes de o anel estourar.
 *
 * Derivado dos orçamentos, NEVER escolhido a dedo (invariante 5): `BUD-tolerated-rtt`
 * é a pior ida e volta que a predição promete cobrir e `BUD-input-rate` diz quantos
 * inputs entram nela. Mais dois intervalos de snapshot de folga, porque o ack volta
 * na cadência de `BUD-snapshot` e NEVER na de tick.
 */
const MAX_PENDING =
  Math.ceil(
    (INVARIANT_BUDGETS['BUD-tolerated-rtt'].value / 1000) *
      INVARIANT_BUDGETS['BUD-input-rate'].value,
  ) +
  2 * Math.ceil(INVARIANT_BUDGETS['BUD-input-rate'].value / INVARIANT_BUDGETS['BUD-snapshot'].value);

/**
 * Escreve o snapshot por cima do estado, reaproveitando os objetos de jogador.
 *
 * ⚠️ `rngState` NEVER vem no fio. Hoje `step` não sorteia nada, então copiar o valor
 * local é inofensivo; no dia em que o sim sortear, a predição diverge do servidor e
 * a correção vira tremor. Isso é buraco de PROTOCOLO, não deste arquivo — está
 * registrado em `docs/WIP.md`.
 */
function writeSnapshotInto(state: GameState, snapshot: AuthoritativeSnapshot, selfId: number): void {
  const players = state.players;
  players.length = snapshot.players.length;

  for (let i = 0; i < snapshot.players.length; i++) {
    const incoming = snapshot.players[i]!;
    let slot = players[i];
    if (slot === undefined) {
      slot = {
        id: 0,
        x: 0,
        z: 0,
        vx: 0,
        vz: 0,
        ix: 0,
        iz: 0,
        facingX: 0,
        facingZ: -1,
        magnetTargetId: 0,
        magnetHeld: false,
        hp: 0,
        lastSeq: 0,
      };
      players[i] = slot;
    }
    slot.id = incoming.id;
    slot.x = incoming.x;
    slot.z = incoming.z;
    slot.vx = incoming.vx;
    slot.vz = incoming.vz;
    slot.hp = incoming.hp;
    // O campo AGORA trafega (`ADR-0017`), e é aqui que a fatia do corpo disputado
    // se paga. Antes estas cinco linhas zeravam: o estado previsto NEVER podia
    // carregar um campo que a autoridade desconhecia. Hoje a autoridade conhece, e
    // zerar seria jogar fora o impulso do puxão a cada `BUD-snapshot` — o robô
    // perderia o voo 15 vezes por segundo enquanto o servidor o mantinha.
    slot.ix = incoming.ix;
    slot.iz = incoming.iz;
    slot.facingX = incoming.facingX;
    slot.facingZ = incoming.facingZ;
    slot.magnetTargetId = incoming.magnetTargetId;
    // `magnetHeld` NEVER trafega, e NEVER precisa: quem o reconstrói é a
    // REEXECUÇÃO. Todo input pendente que o cliente refaz sobre este snapshot
    // reafirma o estado do botão, então o dono chega ao presente com o valor
    // certo. Semear pelo alvo (`magnetTargetId !== 0`) seria pior: com o campo
    // ligado e NADA em alcance, o alvo é 0 e o botão está apertado — e o palpite
    // erraria exatamente no caso que este arquivo existe para não errar.
    slot.magnetHeld = false;
    // O snapshot só carrega o ack de QUEM RECEBE; o dos outros o cliente NEVER sabe.
    // `lastSeq` é escrito pelo sim e nunca lido por ele, então zero aqui não muda nada.
    slot.lastSeq = incoming.id === selfId ? snapshot.ackSeq : 0;
  }

  // O sim exige ordem por id (invariante 1). O servidor já manda ordenado, mas
  // confiar nisso seria deixar o determinismo local depender do outro lado.
  players.sort((a, b) => a.id - b.id);

  // Os corpos, pela mesma regra: reexecutar sem eles faria `acquireTarget` NEVER
  // achar alvo, e o campo do palpite morreria no primeiro tick reexecutado — o
  // oposto exato do que a predição precisa fazer.
  const bodies = state.bodies;
  bodies.length = snapshot.bodies.length;
  for (let i = 0; i < snapshot.bodies.length; i++) {
    const incoming = snapshot.bodies[i]!;
    const kind = bodyKindFromCode(incoming.kind);
    let slot = bodies[i];
    if (slot === undefined) {
      slot = { id: 0, kind: 'light', x: 0, z: 0, vx: 0, vz: 0, heldBy: 0 };
      bodies[i] = slot;
    }
    slot.id = incoming.id;
    // Tipo desconhecido cai em `light`, e NEVER derruba a predição inteira: um
    // corpo com a massa errada erra a trajetória DELE, e um `throw` aqui pararia
    // de prever o avatar também. O render já pula o corpo que não conhece.
    slot.kind = kind ?? 'light';
    slot.x = incoming.x;
    slot.z = incoming.z;
    slot.vx = incoming.vx;
    slot.vz = incoming.vz;
    slot.heldBy = incoming.heldBy;
  }
  bodies.sort((a, b) => a.id - b.id);

  state.tick = snapshot.tick;
}

/**
 * O estado previsto do próprio avatar, e o quanto a última correção doeu.
 *
 * Uso, por tick do cliente:
 *   1. `predict(seq, move)` logo depois de `sendIntent` — avança um tick local.
 *   2. `reconcile(snapshot)` quando a autoridade fala — volta ao que ela disse e
 *      reexecuta o que ela ainda não confirmou.
 */
export class Predictor {
  /** Estado local. Autoritativo até `ackSeq`, palpite depois dele. */
  private readonly state: GameState;
  /** Anel de inputs não confirmados. Alocado uma vez. */
  private readonly pending: PlayerInput[] = [];
  private head = 0;
  private size = 0;
  /**
   * Argumento de `step`, reaproveitado: um array novo por tick seria lixo a 30 Hz.
   *
   * Cabe a sala inteira, e NEVER só o próprio jogador. A intenção dos OUTROS o
   * cliente NEVER tem — mas "sem input" NEVER é neutro para o `step`: ele lê
   * ausência como botão SOLTO, chama `releaseTarget`, e o corpo que o companheiro
   * estava segurando é LANÇADO dentro do estado previsto. O palpite menos errado é
   * "continua como estava", e é o que este arranjo permite dizer.
   */
  private readonly replayInputs: PlayerInput[] = [];
  private readonly replayPool: PlayerInput[] = [];
  private readonly selfPos = { x: 0, z: 0 };
  /** Mesma regra do `selfPos`: reaproveitado, e NEVER alocado por frame. */
  private readonly selfFace = { x: 0, z: -1 };
  private authoritative = false;
  private errorUnits = 0;
  private dropped = 0;

  constructor(
    private readonly selfId: number,
    seed: number,
    /**
     * As zonas de perigo, vindas do `welcome`. Sem elas a predição NEVER empurra,
     * o servidor empurra, e a diferença vira correção a cada snapshot — borracha
     * na tela exatamente dentro da zona, que é onde o jogo mais precisa ser
     * legível. NEVER as leia de `gamedata`: quem decide o conteúdo da sala é o
     * servidor, e adivinhar aqui é divergir no dia em que a flag dele mudar.
     */
    hazards: readonly HazardZone[] = [],
  ) {
    // `createState` e NEVER um literal montado aqui: campo novo no estado do sim
    // tem que chegar à predição de graça, e o literal era exatamente o lugar onde
    // ele NEVER chegaria.
    this.state = createState(seed);
    for (const zone of hazards) this.state.hazards.push(zone);
    for (let i = 0; i < MAX_PENDING; i++) {
      this.pending.push({ playerId: selfId, seq: 0, moveX: 0, moveZ: 0, actions: 0 });
    }
    for (let i = 0; i < balance.room.maxPlayers; i++) {
      this.replayPool.push({ playerId: 0, seq: 0, moveX: 0, moveZ: 0, actions: 0 });
    }
  }

  /** Falso até o primeiro snapshot: antes dele o cliente NEVER sabe onde está. */
  get ready(): boolean {
    return this.authoritative;
  }

  /** Tick que o cliente acredita estar simulando: o autoritativo mais o não confirmado. */
  get tick(): number {
    return this.state.tick;
  }

  /** Inputs enviados que o servidor ainda não confirmou. */
  get pendingCount(): number {
    return this.size;
  }

  /**
   * Inputs jogados fora por estouro do anel. Acima de zero significa rede pior
   * que `BUD-tolerated-rtt` — é sintoma, NEVER erro de programação.
   */
  get droppedInputs(): number {
    return this.dropped;
  }

  /** Distância que a última reconciliação teve que corrigir, em unidades de mundo. */
  get lastErrorUnits(): number {
    return this.errorUnits;
  }

  /** Posição prevista do próprio avatar. Referência reaproveitada: leia os campos. */
  get selfPosition(): Readonly<{ x: number; z: number }> {
    const self = this.findSelf();
    if (self !== undefined) {
      this.selfPos.x = self.x;
      this.selfPos.z = self.z;
    }
    return this.selfPos;
  }

  /**
   * Mira prevista do próprio avatar. Existe pelo mesmo motivo que `selfPosition`: o
   * dono tem a intenção na mão, e a mira é a parte da intenção que se VÊ. Vinda do
   * passado interpolado, o próprio robô giraria um `BUD-interp` depois do dedo —
   * atraso que o dono NEVER precisa pagar, porque quem apontou foi ele.
   */
  get selfFacing(): Readonly<{ x: number; z: number }> {
    const self = this.findSelf();
    if (self !== undefined) {
      this.selfFace.x = self.facingX;
      this.selfFace.z = self.facingZ;
    }
    return this.selfFace;
  }

  /** Estado previsto inteiro. Só leitura: quem escrever aqui quebra a reconciliação. */
  get predictedState(): Readonly<GameState> {
    return this.state;
  }

  /**
   * Guarda o input e avança um tick local com ele.
   *
   * `seq` é o que `sendIntent` devolveu: é por ele que o ack casa o que o servidor
   * aplicou com o que o cliente adivinhou.
   */
  predict(input: PendingInput): void {
    this.push(input);
    // Sem snapshot ainda não existe jogador para mover — o input fica guardado e
    // entra na primeira reexecução, porque o servidor pode já tê-lo aplicado.
    if (!this.authoritative) return;
    this.stepWith(this.slot(this.size - 1));
  }

  /**
   * A autoridade falou: volta ao estado dela e reexecuta o que ela ainda não viu.
   *
   * Devolve a distância corrigida — é ela que vai ao HUD, comparada a
   * `BUD-recon-error`. Zero significa que o palpite estava perfeito.
   */
  reconcile(snapshot: AuthoritativeSnapshot): number {
    const before = this.findSelf();
    const beforeX = before?.x ?? 0;
    const beforeZ = before?.z ?? 0;
    const hadPrediction = this.authoritative && before !== undefined;

    writeSnapshotInto(this.state, snapshot, this.selfId);
    this.authoritative = true;

    // Tudo até o ack já é história: o servidor aplicou e o resultado veio no snapshot.
    // Inclui o input que ele COALESCEU e nunca simulou — o ack pula por cima dele, e
    // é por isso que a comparação é `<=` e NEVER `===`.
    this.dropAcked(snapshot.ackSeq);

    for (let i = 0; i < this.size; i++) this.stepWith(this.slot(i));

    const after = this.findSelf();
    if (!hadPrediction || after === undefined) {
      this.errorUnits = 0;
      return 0;
    }
    this.errorUnits = Math.hypot(after.x - beforeX, after.z - beforeZ);
    return this.errorUnits;
  }

  private findSelf(): GameState['players'][number] | undefined {
    for (const player of this.state.players) {
      if (player.id === this.selfId) return player;
    }
    return undefined;
  }

  private slot(offset: number): PlayerInput {
    return this.pending[(this.head + offset) % MAX_PENDING]!;
  }

  /**
   * Um tick de reexecução: o input REAL do dono, e para cada outro jogador o
   * palpite "continua como estava".
   *
   * Sai na ordem de `state.players`, que o sim mantém ordenada por id — que é
   * exatamente a ordem que `step` exige (invariante 1).
   */
  private stepWith(input: PlayerInput): void {
    let count = 0;
    for (const player of this.state.players) {
      const slot = this.replayPool[count];
      if (slot === undefined) break;
      if (player.id === this.selfId) {
        slot.playerId = input.playerId;
        slot.seq = input.seq;
        slot.moveX = input.moveX;
        slot.moveZ = input.moveZ;
        slot.actions = input.actions;
      } else {
        // Sem direcional, porque adivinhar para onde o companheiro anda é o que a
        // interpolação existe para NEVER fazer (`ADR-0011`). Mas o CAMPO é mantido:
        // a autoridade acabou de dizer que ele está ligado, e desligá-lo aqui
        // inventaria uma soltura que NEVER aconteceu.
        slot.playerId = player.id;
        slot.seq = player.lastSeq;
        slot.moveX = 0;
        slot.moveZ = 0;
        slot.actions = player.magnetTargetId !== 0 ? ACTION_MAGNET : 0;
      }
      this.replayInputs[count] = slot;
      count += 1;
    }
    this.replayInputs.length = count;
    step(this.state, this.replayInputs);
  }

  /**
   * Anel cheio significa rede pior do que a predição promete cobrir. Descarta o
   * MAIS ANTIGO: é o próximo que o servidor vai confirmar de qualquer jeito, e
   * perder um tick de movimento na reexecução é erro pequeno e que se corrige no
   * snapshot seguinte. Parar de prever congelaria o avatar, que é pior.
   */
  private push(input: PendingInput): void {
    if (this.size === MAX_PENDING) {
      this.head = (this.head + 1) % MAX_PENDING;
      this.size -= 1;
      this.dropped += 1;
    }
    const target = this.slot(this.size);
    target.seq = input.seq;
    target.moveX = input.moveX;
    target.moveZ = input.moveZ;
    target.actions = input.actions;
    this.size += 1;
  }

  private dropAcked(ackSeq: number): void {
    while (this.size > 0 && this.slot(0).seq <= ackSeq) {
      this.head = (this.head + 1) % MAX_PENDING;
      this.size -= 1;
    }
  }
}
